// applyImageryCoverage.js — Ticket 02: apply a coverage decision to the imagery TileLayer slot.
//
// Extracted from MapEditorModal.jsx (ticket 02 of the map-editor-modal-refactor) into this
// sibling imagery/ module, alongside `shouldShowImagery.js`/`gibsImageryProvider.js`.

import TileLayer from 'ol/layer/Tile';
import XYZ from 'ol/source/XYZ';
import { shouldShowImagery } from './shouldShowImagery';
import { OVERLAY_TYPES } from '../../schema/mapSchema';

// Ticket 02: lazily creates the imagery `TileLayer`/`XYZ` in `baseTileLayerRef` on first use
// (rather than at map-init time with a placeholder URL), and thereafter just swaps its
// `XYZ` source when the tile URL template changes (new DTG) and toggles visibility.
//
// The imagery layer is given a zIndex (1) that sits *above* the default vector basemap's
// mapbox-layers (unspecified zIndex, i.e. effectively 0) but below the markings/tactical
// layers (2/3/5) and coord-grid overlay (10). Because GIBS imagery is fully-opaque raster
// covering the whole viewport, showing it visually occludes the default basemap underneath
// with no need to separately hide/remove the basemap's own layers — toggling this one layer's
// `visible` flag is sufficient to satisfy "show imagery, else show default basemap".
//
// Ticket 01: `overlayType` re-gates this. Previously this function always showed the GIBS
// layer whenever coverage existed for the current bbox/dtg ("auto-imagery"). It's now only
// consulted at all when the user has explicitly selected the "Satellite" overlay; when the
// overlay is "Mapbox" this short-circuits and *hides* the layer unconditionally — the vector
// basemap underneath is untouched either way, so this alone is sufficient to fall back to it.
export function applyImageryCoverage(map, baseTileLayerRef, bbox, dtgMs, provider, overlayType) {
    if (overlayType !== OVERLAY_TYPES.SATELLITE) {
        if (baseTileLayerRef.current) baseTileLayerRef.current.setVisible(false);
        return;
    }

    const coverage = provider.getCoverage(bbox, dtgMs);
    const showImagery = shouldShowImagery(bbox, dtgMs, provider);

    if (showImagery && coverage.tileUrlTemplate) {
        let layer = baseTileLayerRef.current;
        if (!layer) {
            layer = new TileLayer({
                source: new XYZ({ url: coverage.tileUrlTemplate, crossOrigin: 'anonymous' }),
                zIndex: 1,
            });
            layer.set('id', '__gibs-imagery__');
            baseTileLayerRef.current = layer;
            map.addLayer(layer);
        } else if (layer.getSource()?.getUrls?.()[0] !== coverage.tileUrlTemplate) {
            layer.setSource(new XYZ({ url: coverage.tileUrlTemplate, crossOrigin: 'anonymous' }));
        }
        layer.setVisible(true);
    } else if (baseTileLayerRef.current) {
        baseTileLayerRef.current.setVisible(false);
    }
}
